import { useBankAccountSelectionUrlQuery } from '@/hooks/payment-user/queries/use-bank-account-selection-url.query';
import { useBankAccountSelectionResult } from '@/hooks/payout-account/use-bank-account-selection-result';
import notify from '@/utils/shared/notify';
import { generateBankAccountSelectionCallbackUrl } from '@/utils/payment/payment-user';
import { useLocation } from 'react-router-dom';
import { LoadingButton } from '@/shared/components/ui/buttons/loading-button';
import { useMemo } from 'react';
import { InputErrors } from '@/shared/components/ui/inputs/input-errors';
import { InputProps } from '@/types/form/input-props';
import { ExternalLink, TriangleAlert } from 'lucide-react';
import { useSelectBankAccount } from '../hooks/use-select-bank-account';
import { Badge } from '@/shared/components/ui/badge';
import { bankSelectionError } from '../utils/bank-selection-errors';

export function SelectBankAccount({ errors }: InputProps<string>) {
  const { pathname } = useLocation();
  const { data, isLoading } = useBankAccountSelectionUrlQuery(generateBankAccountSelectionCallbackUrl(pathname));
  const { id, errorCode } = useSelectBankAccount();

  const handleClick = async (e: any) => {
    e.stopPropagation();

    if (!data) {
      notify.error('Det går inte att välja bankkonto just nu. Försök igen senare.');
      return;
    }

    window.location.href = data.url;
  };

  const label = useMemo(() => (errorCode ? 'Försök igen' : id ? 'Välj annat bankkonto' : 'Välj bankkonto'), [id, errorCode]);

  return (
    <div>
      <div className="flex items-center">
        <LoadingButton type="button" isLoading={isLoading} onClick={handleClick}>
          <div className="flex items-center gap-2">
            {label} <ExternalLink />
          </div>
        </LoadingButton>

        {id && <Badge className="ml-auto bg-green-500/20 text-green-700">Bankkonto valt</Badge>}
      </div>
      {errors && <InputErrors errors={errors} />}
      {errorCode && <BankSelectionErrorAlert errorCode={errorCode} />}
    </div>
  );
}

function BankSelectionErrorAlert({ errorCode }: { errorCode: string }) {
  const { title, description } = bankSelectionError(errorCode);

  return (
    <div role="alert" className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3">
      <div className="flex gap-3">
        <TriangleAlert className="shrink-0 mt-0.5 text-amber-500" size={16} aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
}
